import { useEffect, useState } from "react";
import React from "react";

import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { SelectAutocomplete } from "components/forms";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import dayjs from "dayjs";
import { useActionHook } from "hooks";
import { useDateFormatSelect, useReportsActions } from "store";

interface SendActivityEmailContentProps {
  onSubmit: (data: { dateRange: { from: string; to: string }; sendCopy: boolean }) => void;
  onCancel: () => void;
}

export const SendActivityEmailContent: React.FC<SendActivityEmailContentProps> = ({ onSubmit, onCancel }) => {
  const { getAccountRecipientsAction, sendActivityEmailAction } = useReportsActions();
  const [reportType, setReportType] = useState<"summary" | "capital_account_activity">("summary");
  const [selectedUsers, setSelectedUsers] = useState<{ id: number; name: string }[]>([]);
  const [emailsText, setEmailsText] = useState<string>("");
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({ from: "", to: "" });
  const [useCreateDate, setUseCreateDate] = useState<boolean>(false);
  const [sendCopy, setSendCopy] = useState<boolean>(true);

  const { action, data: usersList = [] } = useActionHook(() => getAccountRecipientsAction());

  const dateFormat = useDateFormatSelect();

  const parsedEmails = emailsText
    .split(/[;,]/)
    .map((email) => email.trim())
    .filter(Boolean);

  const changeDateRange = (key: "from" | "to", value: string) => {
    setDateRange((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    await sendActivityEmailAction({
      report_type: reportType,
      date_from: dateRange.from,
      date_to: dateRange.to,
      use_create_date: useCreateDate,
      send_me_copy: sendCopy,
      recipient_ids: selectedUsers.map((user) => user.id),
      recipient_emails: parsedEmails
    });
    onSubmit({ dateRange, sendCopy });
  };

  useEffect(() => {
    action();
  }, []);

  const hasRecipient = selectedUsers.length > 0 || parsedEmails.length > 0 || sendCopy;
  const isSubmitAvailable = Boolean(dateRange.from && dateRange.to && hasRecipient);

  return (
    <Box>
      <Box sx={{ background: "#fff", borderRadius: "10px 10px 0px 0px", padding: "30px 30px 0 30px" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ borderBottom: "1px solid  #DAE0E2", paddingBottom: "5px" }}
        >
          <span>Build Activity Email</span>
          <IconButton onClick={onCancel}>
            <CloseIcon className="icon" />
          </IconButton>
        </Stack>
      </Box>
      <Stack alignItems="stretch" sx={{ p: "20px 30px 30px 30px", width: "500px" }} gap="20px">
        <TextField
          select
          label="Report type"
          value={reportType}
          onChange={(e) => setReportType(e.target.value as "summary" | "capital_account_activity")}
        >
          <MenuItem value="summary">Summary</MenuItem>
          <MenuItem value="capital_account_activity">Capital Account Activity</MenuItem>
        </TextField>
        <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", gap: "16px" }}>
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            value={dateRange.from ? dayjs(dateRange.from) : null}
            label="From"
            maxDate={dateRange.to ? dayjs(dateRange.to) : undefined}
            onChange={(date) => {
              if (date && date.isValid()) {
                changeDateRange("from", date.format("YYYY-MM-DD"));
              }
            }}
            slotProps={{
              inputAdornment: { position: "start" },
              textField: {
                required: true,
                sx: { width: "200px", fontSize: "16px" },
                InputProps: {
                  endAdornment: dateRange.from ? (
                    <InputAdornment position="end">
                      <IconButton onClick={() => changeDateRange("from", "")} edge="end">
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
              },
              popper: {
                sx: {
                  ".MuiPickersCalendarHeader-labelContainer": {
                    fontSize: "0.9rem"
                  }
                }
              }
            }}
          />
          -
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            value={dateRange.to ? dayjs(dateRange.to) : null}
            label="To"
            minDate={dateRange.from ? dayjs(dateRange.from) : undefined}
            onChange={(date) => {
              if (date && date.isValid()) {
                changeDateRange("to", date.format("YYYY-MM-DD"));
              }
            }}
            slotProps={{
              inputAdornment: { position: "start" },
              textField: {
                required: true,
                sx: { width: "200px", fontSize: "16px" },
                InputProps: {
                  endAdornment: dateRange.to ? (
                    <InputAdornment position="end">
                      <IconButton onClick={() => changeDateRange("to", "")} edge="end">
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
              },
              popper: {
                sx: {
                  ".MuiPickersCalendarHeader-labelContainer": {
                    fontSize: "0.9rem"
                  }
                }
              }
            }}
          />
        </Stack>
        <SelectAutocomplete
          sx={{
            "& .MuiAutocomplete-listbox": {
              height: "200px !important"
            }
          }}
          options={usersList}
          value={selectedUsers}
          multiple
          selectAll
          renderOption={(props, option) => <li {...props}>{option.name}</li>}
          getOptionLabel={(option) => option.name}
          compareParam={(option) => option.name}
          setValue={(options) => setSelectedUsers(options)}
          renderInput={(params) => <TextField {...params} label="Users" />}
          disableCloseOnSelect
          renderTags={(tagValue, getTagProps) => {
            return (
              <div style={{ maxHeight: "55px", overflowY: "auto", overflowX: "hidden" }}>
                {tagValue.map((option, index) => (
                  <MaterialTooltip title={option.name} isShow key={option.id}>
                    <Chip sx={{ height: "24px" }} {...getTagProps({ index })} label={option.name} />
                  </MaterialTooltip>
                ))}
              </div>
            );
          }}
        />

        <TextField
          label="Other recipients"
          placeholder="email1@example.com; email2@example.com"
          helperText="Separate multiple email addresses with a comma or semicolon."
          value={emailsText}
          onChange={(e) => setEmailsText(e.target.value)}
        />

        <FormControlLabel
          classes={{ label: "benchmark-graph__switch-label" }}
          control={
            <Switch
              sx={{
                color: "#173c4c",
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#26a6ae"
                },
                "& .MuiSwitch-track": {
                  color: "#f2f2f2"
                }
              }}
              checked={useCreateDate}
              onChange={(e, checked) => setUseCreateDate(checked)}
            />
          }
          label="Use creation date"
          labelPlacement="end"
        />

        <FormControlLabel
          classes={{ label: "benchmark-graph__switch-label" }}
          control={
            <Switch
              sx={{
                color: "#173c4c",
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#26a6ae"
                },
                "& .MuiSwitch-track": {
                  color: "#f2f2f2"
                }
              }}
              checked={sendCopy}
              onChange={(e, checked) => setSendCopy(checked)}
            />
          }
          label="Send me a copy"
          labelPlacement="end"
        />

        <Stack direction="row" justifyContent="flex-end" gap="20px">
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          <Button disabled={!isSubmitAvailable} variant="contained" onClick={handleSubmit}>
            Send
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
